// imports
import React, { useState } from 'react'
import { createPortal } from 'react-dom'
import { Button } from '@bosch/react-frok'

// components
import CustomMarkdown from '@/ui/components/CustomMarkdown/CustomMarkdown'
import MessageSources from '@/ui/components/ChatMessageTypes/MessageSources/MessageSources'
import ChatFeatures from '@/ui/components/ChatFeatures/ChatFeatures'
import MessageWrapper from '@/ui/components/MessageWrapper/MessageWrapper'

// hooks & utils
import useWindowSize from '@/hooks/useWindowSize'
import type { AssistantChatMessage } from '@/ui/components/ChatMessageTypes/Assistant/types/AssistantMessageTypes'
import type { ChatFeatureValue } from '@/ui/components/ChatFeatures/types/ChatFeaturesTypes'
import type { OutputFeatures } from '@/shared/types/messageTools'

// styles
import './SolutionCard.scss'

export interface SolutionCardProps {
    msg: AssistantChatMessage
    features?: Record<string, ChatFeatureValue>
    isDarkTheme?: boolean
    onExpand: () => void
}

const SolutionCardComponent = ({
    msg,
    features,
    onExpand,
}: SolutionCardProps) => {
    const content = msg.content || msg.message || ''

    return (
        <>
            <div className="card-content" onClick={(e) => { e.stopPropagation(); onExpand() }}>
                <CustomMarkdown>{content}</CustomMarkdown>
                <MessageSources message={msg} />
            </div>
            <ChatFeatures features={features} message={msg} />
        </>
    )
}

export const SolutionCard = React.memo(SolutionCardComponent)

export interface SolutionCardWrapperProps {
    msg: AssistantChatMessage
    features?: Record<string, ChatFeatureValue>
    outputFeatures?: OutputFeatures
    isDarkTheme?: boolean
    onExpandChange?: (expanded: boolean) => void
}

const SolutionCardWrapperComponent = ({
    msg,
    features,
    outputFeatures,
    isDarkTheme,
    onExpandChange,
}: SolutionCardWrapperProps) => {
    const { isMobile } = useWindowSize()
    const [expandedMessage, setExpandedMessage] = useState<AssistantChatMessage | null>(null)

    const content = msg.content || msg.message || ''

    const handleExpand = (msg: AssistantChatMessage) => {
        setExpandedMessage(msg)
        onExpandChange?.(true)
    }

    const handleClose = () => {
        setExpandedMessage(null)
        onExpandChange?.(false)
    }

    return (
        <>
            <SolutionCard
                msg={msg}
                features={features}
                isDarkTheme={isDarkTheme}
                onExpand={() => handleExpand(msg)}
            />
            {expandedMessage && createPortal(
                (() => {
                    const cardDetail = (
                        <div className="card-detail" onClick={(e) => e.stopPropagation()}>
                            <MessageWrapper
                                content={content}
                                hasContent={!!content}
                                outputFeatures={outputFeatures}
                                messageType="assistant"
                            >
                                <div className="card-detail__header">
                                    <Button
                                        icon={{ iconName: 'close', title: 'Close' }}
                                        mode="integrated"
                                        onClick={handleClose}
                                    />
                                </div>
                                <div className="card-detail__body">
                                    <CustomMarkdown>{content}</CustomMarkdown>
                                    <MessageSources message={expandedMessage} />
                                </div>
                            </MessageWrapper>
                        </div>
                    )
                    const overlay = isMobile
                        ? <div className="card-fullscreen-wrapper" onClick={handleClose}>{cardDetail}</div>
                        : <div className="card-overlay" onClick={handleClose}>{cardDetail}</div>
                    // In the body, outside of the element with the dark mode class: it gets its own. `display: contents`,
                    // as the class also sets a black background that would hide the blur of the overlay
                    return isDarkTheme ? <div className="-dark-mode" style={{ display: 'contents' }}>{overlay}</div> : overlay
                })(),
                document.body
            )}
        </>
    )
}

const SolutionCardWrapper = React.memo(SolutionCardWrapperComponent)

export default SolutionCardWrapper
